import { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';

// ─── Ghost Navigation ─────────────────────────────────────────────────────────
function GhostNav({ onPortalClick }: { onPortalClick: () => void }) {
  const [visible, setVisible] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showNav = useCallback(() => {
    setVisible(true);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      if (window.scrollY < 80) setVisible(false);
    }, 2200);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 80);
      if (y > 80) setVisible(true);
    };
    const handleMouseMove = (e: MouseEvent) => {
      if (e.clientY < 90) showNav();
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('mousemove', handleMouseMove);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [showNav]);

  const navLinks = [
    { label: 'Services', href: '#services' },
    { label: 'Certifications', href: '#certifications' },
    { label: 'About', href: '#about' },
    { label: 'Standards', href: '#impact' },
    { label: 'Contact', href: '#contact' },
  ];

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-500"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(-100%)',
        background: scrolled ? 'rgba(8,8,8,0.92)' : 'transparent',
        backdropFilter: scrolled ? 'blur(20px)' : 'none',
        borderBottom: scrolled ? '1px solid rgba(46,46,46,0.8)' : '1px solid transparent',
      }}
    >
      <div className="px-5 md:px-16 py-4 md:py-5 flex items-center justify-between">
        <a href="#" className="flex items-center gap-3 group cursor-pointer">
          <div className="w-8 h-8 border border-[#00E5FF] flex items-center justify-center flex-shrink-0">
            <div className="flex items-end gap-[2px]">
              <div className="w-[3px] h-[8px] bg-[#00E5FF]"></div>
              <div className="w-[3px] h-[13px] bg-[#00E5FF]"></div>
              <div className="w-[3px] h-[10px] bg-[#00E5FF]"></div>
              <div className="w-[3px] h-[15px] bg-[#00E5FF]"></div>
              <div className="w-[3px] h-[6px] bg-[#00E5FF]"></div>
            </div>
          </div>
          <div>
            <div className="text-[13px] font-bold tracking-[0.18em] text-[#F5F5F5] uppercase" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Sure Kleen
            </div>
            <div className="text-[8px] font-light tracking-[0.3em] text-[#6B6B6B] uppercase" style={{ fontFamily: "'Space Mono', monospace" }}>
              Building&nbsp;Services,&nbsp;Inc.
            </div>
          </div>
        </a>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-8 lg:gap-10">
          {navLinks.map((link) => (
            <a key={link.label} href={link.href}
              className="relative text-[11px] tracking-[0.22em] text-[#6B6B6B] hover:text-[#F5F5F5] transition-colors duration-300 uppercase group cursor-pointer"
              style={{ fontFamily: "'Space Mono', monospace" }}
            >
              {link.label}
              <span className="absolute -bottom-1 left-0 w-0 h-[1px] bg-[#00E5FF] group-hover:w-full transition-all duration-400"></span>
            </a>
          ))}
          <button onClick={onPortalClick}
            className="px-5 py-2.5 border border-[#00E5FF] text-[#00E5FF] text-[10px] tracking-[0.25em] uppercase hover:bg-[#00E5FF] hover:text-[#080808] transition-all duration-300 whitespace-nowrap cursor-pointer"
            style={{ fontFamily: "'Space Mono', monospace" }}
          >
            Client Portal
          </button>
        </div>

        {/* Mobile hamburger */}
        <button
          className="md:hidden w-9 h-9 flex items-center justify-center text-[#F5F5F5] cursor-pointer"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-line'} text-[22px]`}></i>
        </button>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="md:hidden bg-[#080808]/95 backdrop-blur-xl border-t border-[#2E2E2E] px-5 py-6 flex flex-col gap-5">
          {navLinks.map((link) => (
            <a key={link.label} href={link.href}
              onClick={() => setMenuOpen(false)}
              className="text-[12px] tracking-[0.25em] text-[#6B6B6B] hover:text-[#F5F5F5] uppercase transition-colors cursor-pointer"
              style={{ fontFamily: "'Space Mono', monospace" }}
            >
              {link.label}
            </a>
          ))}
          <button onClick={() => { setMenuOpen(false); onPortalClick(); }}
            className="mt-2 px-6 py-3 border border-[#00E5FF] text-[#00E5FF] text-[10px] tracking-[0.25em] uppercase whitespace-nowrap cursor-pointer w-full"
            style={{ fontFamily: "'Space Mono', monospace" }}
          >
            Client Portal
          </button>
        </div>
      )}
    </nav>
  );
}

// ─── Ticker Tape ─────────────────────────────────────────────────────────────
function TickerTape() {
  const items = [
    'NAICS 561720 — Janitorial Services',
    'SBA 8(a) Certified',
    'MBE · DBE · Minority Owned',
    'Cage Code: 38BS9',
    'DUNS: 171838126',
    'Minneapolis–St. Paul Metro',
    'NAICS 236220-15 — Construction Site Clean-Up',
    'SIC 4581 — Aircraft Janitorial',
    'Bonded & Insured',
    'Targeted Group Certified',
  ];
  const doubled = [...items, ...items];

  return (
    <div className="w-full overflow-hidden border-t border-b border-[#2E2E2E] py-3 bg-[#080808]">
      <div className="flex gap-16 whitespace-nowrap" style={{ animation: 'ticker 40s linear infinite' }}>
        {doubled.map((item, i) => (
          <span key={i} className="text-[10px] tracking-[0.25em] text-[#6B6B6B] uppercase flex items-center gap-4" style={{ fontFamily: "'Space Mono', monospace" }}>
            <span className="w-1 h-1 bg-[#00E5FF] inline-block rounded-full flex-shrink-0"></span>
            {item}
          </span>
        ))}
      </div>
    </div>
  );
}

// ─── Section Label ────────────────────────────────────────────────────────────
function SectionLabel({ text, index }: { text: string; index: string }) {
  return (
    <div className="flex items-center gap-4 md:gap-6 mb-10 md:mb-16">
      <span className="text-[10px] tracking-[0.3em] text-[#00E5FF] uppercase" style={{ fontFamily: "'Space Mono', monospace" }}>[{index}]</span>
      <div className="h-[1px] w-8 md:w-12 bg-[#2E2E2E]"></div>
      <span className="text-[10px] tracking-[0.3em] text-[#6B6B6B] uppercase" style={{ fontFamily: "'Space Mono', monospace" }}>{text}</span>
    </div>
  );
}

// ─── Animated Counter ─────────────────────────────────────────────────────────
function AnimatedStat({ value, label, suffix = '' }: { value: number; label: string; suffix?: string }) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          let start = 0;
          const duration = 1800;
          const step = (timestamp: number) => {
            if (!start) start = timestamp;
            const progress = Math.min((timestamp - start) / duration, 1);
            setCount(Math.floor(progress * value));
            if (progress < 1) requestAnimationFrame(step);
          };
          requestAnimationFrame(step);
        }
      },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [value]);

  return (
    <div ref={ref} className="border-l border-[#2E2E2E] pl-5 md:pl-8">
      <div className="text-[40px] md:text-[64px] font-black text-[#F5F5F5] leading-none mb-2" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
        {count}{suffix}
      </div>
      <div className="text-[9px] md:text-[10px] tracking-[0.25em] text-[#6B6B6B] uppercase" style={{ fontFamily: "'Space Mono', monospace" }}>{label}</div>
    </div>
  );
}

// ─── Service Gallery Card ─────────────────────────────────────────────────────
function ServiceCard({ index, title, naics, description, image, slug }: {
  index: string; title: string; naics: string; description: string; image: string; slug: string;
}) {
  const [hovered, setHovered] = useState(false);
  return (
    <Link
      to={`/services/${slug}`}
      className="relative overflow-hidden cursor-pointer group"
      style={{ border: '1px solid #2E2E2E', transition: 'border-color 0.4s ease', borderColor: hovered ? '#00E5FF' : '#2E2E2E' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="w-full h-52 md:h-64 overflow-hidden relative">
        <img src={image} alt={title} className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105" />
        <div className="absolute inset-0 transition-opacity duration-500" style={{ background: 'linear-gradient(to bottom, transparent 30%, rgba(8,8,8,0.95) 100%)' }}></div>
      </div>
      <div className="p-5 md:p-8 bg-[#080808]">
        <div className="flex items-start justify-between mb-3 md:mb-4">
          <span className="text-[10px] tracking-[0.3em] text-[#00E5FF] uppercase" style={{ fontFamily: "'Space Mono', monospace" }}>{index}</span>
          <span className="text-[9px] tracking-[0.2em] text-[#6B6B6B] uppercase" style={{ fontFamily: "'Space Mono', monospace" }}>{naics}</span>
        </div>
        <h3 className="text-[18px] md:text-[22px] font-bold text-[#F5F5F5] mb-3 md:mb-4 leading-tight" style={{ fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '0.04em' }}>{title}</h3>
        <p className="text-[12px] md:text-[13px] font-light leading-[1.9] text-[#6B6B6B]" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{description}</p>
        <div className="mt-4 md:mt-6 flex items-center gap-3 text-[10px] tracking-[0.25em] uppercase transition-colors duration-300" style={{ color: hovered ? '#00E5FF' : '#6B6B6B', fontFamily: "'Space Mono', monospace" }}>
          <span>Learn More</span>
          <i className="ri-arrow-right-line text-[14px]"></i>
        </div>
      </div>
    </Link>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function HomePage() {
  const [showPortal, setShowPortal] = useState(false);
  const [heroSlide, setHeroSlide] = useState(0);

  const heroSlides = [
    "https://public.readdy.ai/ai/img_res/92b08b67ca1563bf949069791e30c440.jpg",
    "https://readdy.ai/api/search-image?query=professional-facility-management-team-in-sleek-black-uniforms-operating-industrial-floor-polishing-machine-on-vast-gleaming-white-marble-floor-of-luxury-corporate-headquarters-dramatic-overhead-lighting-creating-mirror-reflections-cinematic-wide-angle-shot-ultra-high-definition-monochrome-industrial-aesthetic&width=1920&height=1080&seq=hero-jan-slide-02&orientation=landscape",
    "https://readdy.ai/api/search-image?query=pristine-glass-curtain-wall-skyscraper-exterior-window-cleaning-crew-suspended-on-professional-rigging-system-dramatic-aerial-perspective-city-skyline-below-cinematic-high-contrast-black-and-white-photography-precision-facility-services-ultra-modern-architecture-sharp-geometric-lines&width=1920&height=1080&seq=hero-jan-slide-03&orientation=landscape",
    "https://readdy.ai/api/search-image?query=immaculate-hospital-corridor-after-professional-deep-cleaning-gleaming-white-floors-reflecting-overhead-fluorescent-lights-sterile-pristine-environment-cinematic-perspective-vanishing-point-composition-ultra-modern-healthcare-facility-monochrome-high-contrast-architectural-photography-precision-janitorial-excellence&width=1920&height=1080&seq=hero-jan-slide-04&orientation=landscape",
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setHeroSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [heroSlides.length]);

  const videoRef = useRef<HTMLIFrameElement>(null);

  const services = [
    {
      index: '01', title: 'Commercial & Residential Cleaning', naics: 'NAICS 561720',
      description: 'Precision janitorial and custodial services for offices, retail, and residential properties across the Minneapolis–St. Paul metro.',
      image: 'https://readdy.ai/api/search-image?query=ultra-modern-corporate-office-interior-with-polished-floors-glass-walls-and-minimalist-furniture-black-and-white-architectural-photography-high-contrast-professional-commercial-space-clean-lines-geometric-shadows&width=600&height=400&seq=svc-01&orientation=landscape',
      slug: 'commercial-cleaning',
    },
    {
      index: '02', title: 'Post-Construction Clean-Up', naics: 'NAICS 236220-15',
      description: 'Specialized construction site clean-up removing debris, dust, and residue to deliver move-in ready spaces with surgical precision.',
      image: 'https://readdy.ai/api/search-image?query=modern-construction-site-interior-with-concrete-walls-and-industrial-lighting-black-and-white-photography-architectural-minimalism-raw-concrete-texture-high-contrast-monochrome&width=600&height=400&seq=svc-02&orientation=landscape',
      slug: 'post-construction',
    },
    {
      index: '03', title: 'Healthcare & Medical Facility Cleaning', naics: 'NAICS 561720',
      description: 'Sterile, compliance-grade cleaning for hospitals, clinics, and medical offices meeting the highest hygiene and safety standards.',
      image: 'https://readdy.ai/api/search-image?query=pristine-modern-hospital-corridor-with-gleaming-white-floors-and-sterile-medical-environment-black-and-white-photography-healthcare-facility-minimalism-high-contrast-monochrome-professional-clean&width=600&height=400&seq=svc-03-healthcare&orientation=landscape',
      slug: 'healthcare-facility',
    },
    {
      index: '04', title: 'Strip & Wax / Carpet Cleaning', naics: 'SIC 7349',
      description: 'Professional floor care — strip and wax services and deep carpet cleaning extending the life and appearance of your flooring.',
      image: 'https://readdy.ai/api/search-image?query=polished-marble-floor-in-luxury-corporate-lobby-with-reflections-and-geometric-patterns-black-and-white-architectural-photography-minimalist-high-contrast-monochrome&width=600&height=400&seq=svc-04&orientation=landscape',
      slug: 'strip-wax-carpet',
    },
    {
      index: '05', title: 'Window Cleaning', naics: 'SIC 7349',
      description: 'Interior and exterior window cleaning for commercial and residential buildings — crystal-clear results at any height.',
      image: 'https://readdy.ai/api/search-image?query=glass-curtain-wall-building-facade-with-geometric-reflections-of-sky-and-city-black-and-white-architectural-photography-minimalist-high-contrast-monochrome-sharp-lines&width=600&height=400&seq=svc-05&orientation=landscape',
      slug: 'window-cleaning',
    },
    {
      index: '06', title: 'Snow Removal & Landscape', naics: 'NAICS 561720',
      description: 'Reliable seasonal snow and ice removal plus year-round grounds maintenance for pristine facility exteriors.',
      image: 'https://readdy.ai/api/search-image?query=modern-corporate-building-exterior-in-winter-with-clean-snow-covered-grounds-and-geometric-architecture-black-and-white-photography-minimalist-high-contrast-monochrome&width=600&height=400&seq=svc-06&orientation=landscape',
      slug: 'snow-removal',
    },
    {
      index: '07', title: 'Foreclosed Home Maintenance', naics: 'SIC 7349',
      description: 'Cleaning and maintenance of foreclosed and vacant properties, restoring them to market-ready condition.',
      image: 'https://readdy.ai/api/search-image?query=empty-modern-residential-interior-with-clean-white-walls-and-hardwood-floors-black-and-white-architectural-photography-minimalist-high-contrast-monochrome-vacant-home&width=600&height=400&seq=svc-07&orientation=landscape',
      slug: 'foreclosed-home',
    },
    {
      index: '08', title: 'Facility Management', naics: 'NAICS 561720',
      description: 'End-to-end facility oversight integrating scheduling, compliance, and real-time reporting for enterprise clients.',
      image: 'https://readdy.ai/api/search-image?query=futuristic-building-management-control-room-with-screens-and-modern-technology-black-and-white-photography-minimalist-high-contrast-monochrome-corporate-facility&width=600&height=400&seq=svc-08&orientation=landscape',
      slug: 'facility-management',
    },
  ];

  const certifications = [
    { code: 'SBA 8(a)', label: 'Small Business Administration', icon: 'ri-government-line' },
    { code: 'MBE', label: 'Minority Business Enterprise', icon: 'ri-medal-line' },
    { code: 'DBE', label: 'Disadvantaged Business Enterprise', icon: 'ri-shield-star-line' },
    { code: 'Minority Owned', label: 'Small Business Designation', icon: 'ri-award-line' },
    { code: 'Bonded & Insured', label: 'Full Coverage Protection', icon: 'ri-lock-line' },
    { code: 'Targeted Group', label: 'State Certified', icon: 'ri-group-line' },
  ];

  return (
    <div className="bg-[#080808] text-[#F5F5F5] overflow-x-hidden" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
      <style>{`
        @keyframes ticker {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(40px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes cyanPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
        @keyframes scrollBar {
          0% { top: -40%; }
          100% { top: 100%; }
        }
        @keyframes heroReveal {
          from { opacity: 0; transform: translateY(60px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes lineExpand {
          from { transform: scaleX(0); transform-origin: left; }
          to { transform: scaleX(1); transform-origin: left; }
        }
        @keyframes heroSlideIn {
          from { opacity: 0; transform: scale(1.06); }
          to { opacity: 1; transform: scale(1.03); }
        }
        @keyframes heroKenBurns {
          from { transform: scale(1.03); }
          to { transform: scale(1.08); }
        }
        .fade-up-1 { animation: fadeUp 1s ease 0.2s both; }
        .fade-up-2 { animation: fadeUp 1s ease 0.45s both; }
        .fade-up-3 { animation: heroReveal 1.1s cubic-bezier(0.16,1,0.3,1) 0.65s both; }
        .fade-up-4 { animation: fadeUp 1s ease 0.85s both; }
        .cta-reveal { animation: fadeUp 1s ease 1.05s both; }
        .cyan-dot { animation: cyanPulse 2.5s ease infinite; }
        .line-expand { animation: lineExpand 1.2s ease 0.3s both; }
        .hero-slide-active { animation: heroSlideIn 1.2s cubic-bezier(0.16,1,0.3,1) both; }
        .hero-slide-ken { animation: heroKenBurns 6s ease-in-out both; }
      `}</style>

      <GhostNav onPortalClick={() => setShowPortal(true)} />

      {/* ── HERO ── */}
      <section className="relative w-full h-screen min-h-[600px] overflow-hidden">
        {/* Crossfade Slideshow Background */}
        <div className="absolute inset-0 w-full h-full">
          {heroSlides.map((src, i) => (
            <div
              key={i}
              className="absolute inset-0 w-full h-full transition-opacity duration-[1800ms] ease-in-out"
              style={{ opacity: heroSlide === i ? 1 : 0 }}
            >
              <img
                src={src}
                alt="Sure Kleen Facility Management"
                className="w-full h-full object-cover object-center hero-slide-ken"
                style={{ animationPlayState: heroSlide === i ? 'running' : 'paused' }}
              />
            </div>
          ))}
        </div>

        {/* Overlays */}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(8,8,8,0.82) 0%, rgba(8,8,8,0.48) 50%, rgba(8,8,8,0.65) 100%)' }}></div>
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(8,8,8,0.92) 0%, transparent 45%)' }}></div>
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: 'linear-gradient(rgba(245,245,245,0.8) 1px, transparent 1px), linear-gradient(90deg, rgba(245,245,245,0.8) 1px, transparent 1px)',
          backgroundSize: '80px 80px',
        }}></div>
        <div className="absolute top-0 left-0 right-0 h-[1px]" style={{ background: 'linear-gradient(90deg, transparent, rgba(0,229,255,0.4), transparent)' }}></div>
        <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-[#2E2E2E]/60"></div>

        {/* Slide indicators */}
        <div className="absolute bottom-16 md:bottom-24 left-1/2 -translate-x-1/2 flex items-center gap-3 z-10">
          {heroSlides.map((_, i) => (
            <button
              key={i}
              onClick={() => setHeroSlide(i)}
              className="cursor-pointer transition-all duration-500"
              style={{
                width: heroSlide === i ? '32px' : '6px',
                height: '2px',
                background: heroSlide === i ? '#00E5FF' : 'rgba(245,245,245,0.25)',
              }}
            />
          ))}
        </div>

        {/* Hero Content */}
        <div className="relative h-full flex flex-col justify-between px-5 sm:px-10 md:px-20 py-6 md:py-20 w-full">

          {/* Top row */}
          <div className="fade-up-1 flex items-start justify-between w-full pt-20 md:pt-24">
            <div className="flex items-center gap-3 md:gap-4">
              <div className="w-8 h-8 md:w-10 md:h-10 border border-[#00E5FF] flex items-center justify-center flex-shrink-0" style={{ boxShadow: '0 0 32px rgba(0,229,255,0.18)' }}>
                <div className="flex items-end gap-[2px]">
                  <div className="w-[3px] h-[8px] md:w-[4px] md:h-[10px] bg-[#00E5FF]"></div>
                  <div className="w-[3px] h-[12px] md:w-[4px] md:h-[16px] bg-[#00E5FF]"></div>
                  <div className="w-[3px] h-[9px] md:w-[4px] md:h-[12px] bg-[#00E5FF]"></div>
                  <div className="w-[3px] h-[14px] md:w-[4px] md:h-[18px] bg-[#00E5FF]"></div>
                  <div className="w-[3px] h-[6px] md:w-[4px] md:h-[8px] bg-[#00E5FF]"></div>
                </div>
              </div>
              <div>
                <div className="text-[16px] md:text-[20px] font-black tracking-[0.14em] text-white uppercase leading-none" style={{ fontFamily: "'Barlow Condensed', sans-serif", textShadow: '0 2px 20px rgba(0,0,0,0.5)' }}>
                  Sure Kleen
                </div>
                <div className="text-[8px] md:text-[9px] font-light tracking-[0.3em] text-white/50 uppercase mt-1" style={{ fontFamily: "'Space Mono', monospace" }}>
                  Building&nbsp;Services,&nbsp;Inc.
                </div>
              </div>
            </div>

            {/* Badge — hidden on small screens */}
            <div className="hidden sm:flex items-center gap-3 px-4 md:px-5 py-2 md:py-2.5" style={{ border: '1px solid rgba(0,229,255,0.25)', background: 'rgba(8,8,8,0.45)', backdropFilter: 'blur(12px)' }}>
              <span className="cyan-dot w-1.5 h-1.5 bg-[#00E5FF] rounded-full flex-shrink-0"></span>
              <span className="text-[8px] md:text-[9px] tracking-[0.25em] md:tracking-[0.3em] text-white/70 uppercase" style={{ fontFamily: "'Space Mono', monospace" }}>
                SBA 8(a) · MBE · DBE · Bonded &amp; Insured
              </span>
            </div>
          </div>

          {/* Center: Main headline */}
          <div className="flex flex-col md:flex-row items-start md:items-end justify-between w-full gap-8 md:gap-0">
            <div className="w-full md:max-w-[760px]">
              <div className="fade-up-2 flex items-center gap-4 md:gap-5 mb-6 md:mb-10">
                <div className="line-expand h-[1px] w-10 md:w-16 bg-[#00E5FF]"></div>
                <span className="text-[9px] md:text-[10px] tracking-[0.35em] md:tracking-[0.45em] text-[#00E5FF] uppercase" style={{ fontFamily: "'Space Mono', monospace" }}>
                  Minneapolis–St. Paul · Est. 2008
                </span>
              </div>

              <h1
                className="fade-up-3 text-white leading-[0.9] mb-6 md:mb-10"
                style={{
                  fontFamily: "'Barlow Condensed', sans-serif",
                  fontWeight: 900,
                  fontSize: 'clamp(48px, 10vw, 120px)',
                  letterSpacing: '-0.02em',
                  textShadow: '0 4px 40px rgba(0,0,0,0.4)',
                }}
              >
                PRECISION<br />
                FACILITY<br />
                <span style={{ color: '#00E5FF', textShadow: '0 0 60px rgba(0,229,255,0.35)' }}>MANAGEMENT</span>
              </h1>

              <p className="fade-up-4 text-[13px] md:text-[14px] font-light leading-[2] text-white/60 max-w-[480px] tracking-wide mb-8 md:mb-12" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Minority-owned. Federally certified. Delivering commercial &amp; residential cleaning, post-construction, healthcare facility services, and full facility oversight across the Twin Cities metro.
              </p>

              <div className="cta-reveal flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
                <a href="#services"
                  className="px-8 md:px-10 py-3.5 md:py-4 bg-[#00E5FF] text-[#080808] text-[10px] font-bold tracking-[0.3em] uppercase hover:bg-white transition-all duration-300 whitespace-nowrap cursor-pointer"
                  style={{ fontFamily: "'Space Mono', monospace" }}
                >
                  View Services
                </a>
                <button onClick={() => setShowPortal(true)}
                  className="px-8 md:px-10 py-3.5 md:py-4 text-[10px] tracking-[0.3em] uppercase transition-all duration-300 whitespace-nowrap cursor-pointer"
                  style={{
                    fontFamily: "'Space Mono', monospace",
                    border: '1px solid rgba(245,245,245,0.3)',
                    color: 'rgba(245,245,245,0.7)',
                    background: 'rgba(8,8,8,0.3)',
                    backdropFilter: 'blur(8px)',
                  }}
                  onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(245,245,245,0.8)'; (e.currentTarget as HTMLButtonElement).style.color = '#F5F5F5'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(245,245,245,0.3)'; (e.currentTarget as HTMLButtonElement).style.color = 'rgba(245,245,245,0.7)'; }}
                >
                  Client Portal
                </button>
              </div>
            </div>

            {/* Right: Credential strip — hidden on mobile */}
            <div className="hidden md:flex flex-col gap-8 items-end pb-2 flex-shrink-0">
              {[
                { value: 'Cage', sub: '38BS9' },
                { value: 'DUNS', sub: '171838126' },
                { value: '8(a)', sub: 'SBA Certified' },
              ].map((s) => (
                <div key={s.sub} className="text-right" style={{ borderRight: '1px solid rgba(0,229,255,0.25)', paddingRight: '20px' }}>
                  <div className="text-[11px] font-bold tracking-[0.2em] text-white/40 uppercase leading-none mb-1" style={{ fontFamily: "'Space Mono', monospace" }}>
                    {s.value}
                  </div>
                  <div className="text-[18px] font-black text-white leading-none" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                    {s.sub}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Bottom row */}
          <div className="flex items-end justify-between w-full">
            <div className="flex items-center gap-4">
              <div className="w-[1px] h-10 md:h-12 bg-white/20 relative overflow-hidden">
                <div className="absolute top-0 w-full bg-[#00E5FF]" style={{ height: '40%', animation: 'scrollBar 1.8s ease-in-out infinite' }}></div>
              </div>
              <span className="text-[9px] tracking-[0.35em] text-white/30 uppercase" style={{ fontFamily: "'Space Mono', monospace" }}>Scroll</span>
            </div>
            <div className="text-right hidden sm:block">
              <div className="text-[9px] tracking-[0.3em] text-white/25 uppercase" style={{ fontFamily: "'Space Mono', monospace" }}>
                44.9778° N · 93.2650° W · Minneapolis, MN
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── TICKER TAPE ── */}
      <TickerTape />

      {/* ── SERVICES GALLERY ── */}
      <section id="services" className="bg-[#080808] py-20 md:py-40 px-5 sm:px-10 md:px-20">
        <div className="max-w-[1600px] mx-auto">
          <SectionLabel text="Core Competencies" index="01" />
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-24">
            <h2 className="text-[56px] sm:text-[72px] md:text-[96px] font-black leading-[0.9] text-[#F5F5F5]" style={{ fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '-0.02em' }}>
              THE PRECISION<br /><span style={{ color: '#00E5FF' }}>STANDARD</span>
            </h2>
            <p className="text-[13px] font-light leading-[2] text-[#6B6B6B] md:max-w-[360px] md:text-right" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Eight specialized service verticals engineered for corporate, government, and residential environments.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-[1px] bg-[#2E2E2E]">
            {services.map((svc) => (<ServiceCard key={svc.index} {...svc} />))}
          </div>
        </div>
      </section>

      {/* ── CERTIFICATIONS ── */}
      <section id="certifications" className="bg-[#0C0C0C] py-20 md:py-40 px-5 sm:px-10 md:px-20">
        <div className="max-w-[1600px] mx-auto">
          <SectionLabel text="Capability Statement" index="02" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-32 items-start mb-16 md:mb-32">
            <h2 className="text-[56px] sm:text-[72px] md:text-[96px] font-black leading-[0.9] text-[#F5F5F5]" style={{ fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '-0.02em' }}>
              CERTIFIED.<br /><span style={{ color: '#00E5FF' }}>TRUSTED.</span>
            </h2>
            <div className="pt-0 md:pt-4">
              <p className="text-[14px] md:text-[15px] font-light leading-[2] text-[#6B6B6B] mb-6 md:mb-8" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                As a federally recognized minority-owned small business, Sure Kleen holds the certifications that matter most to government agencies, prime contractors, and enterprise clients.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { label: 'Cage Code', value: '38BS9' },
                  { label: 'DUNS Number', value: '171838126' },
                  { label: 'NAICS', value: '561720' },
                  { label: 'NAICS', value: '236220-15' },
                  { label: 'SIC', value: '4581' },
                  { label: 'SIC', value: '7349' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center justify-between border-b border-[#2E2E2E] py-3 md:py-4">
                    <span className="text-[10px] tracking-[0.25em] text-[#6B6B6B] uppercase" style={{ fontFamily: "'Space Mono', monospace" }}>{item.label}</span>
                    <span className="text-[12px] md:text-[13px] font-medium text-[#F5F5F5]" style={{ fontFamily: "'Space Mono', monospace" }}>{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-[1px] bg-[#2E2E2E]">
            {certifications.map((cert, i) => (
              <div key={i} className="bg-[#080808] p-6 md:p-10 group hover:bg-[#1C1C1C] transition-colors duration-300 cursor-pointer">
                <div className="w-10 h-10 flex items-center justify-center mb-5 md:mb-8">
                  <i className={`${cert.icon} text-[24px] md:text-[28px] text-[#2E2E2E] group-hover:text-[#00E5FF] transition-colors duration-300`}></i>
                </div>
                <div className="text-[16px] md:text-[18px] font-bold text-[#F5F5F5] mb-2 leading-tight" style={{ fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '0.04em' }}>{cert.code}</div>
                <div className="text-[9px] md:text-[10px] tracking-[0.15em] text-[#6B6B6B] uppercase leading-relaxed" style={{ fontFamily: "'Space Mono', monospace" }}>{cert.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── ABOUT ── */}
      <section id="about" className="bg-[#080808] py-20 md:py-40 px-5 sm:px-10 md:px-20">
        <div className="max-w-[1600px] mx-auto">
          <SectionLabel text="About the Firm" index="03" />
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
            <div className="md:col-span-5">
              <div className="relative overflow-hidden" style={{ border: '1px solid #2E2E2E' }}>
                <img
                  src="https://readdy.ai/api/search-image?query=minneapolis-downtown-skyline-at-dusk-with-modern-glass-skyscrapers-reflecting-golden-light-dramatic-architectural-photography-high-contrast-monochromatic-urban-landscape-sharp-geometric-lines-professional-commercial&width=700&height=900&seq=about-pmi-001&orientation=portrait"
                  alt="Minneapolis Skyline"
                  className="w-full h-[320px] sm:h-[480px] md:h-[700px] object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#080808]/80 via-transparent to-transparent"></div>
                <div className="absolute bottom-6 md:bottom-8 left-6 md:left-8 right-6 md:right-8">
                  <div className="text-[10px] tracking-[0.3em] text-[#00E5FF] uppercase mb-2" style={{ fontFamily: "'Space Mono', monospace" }}>Minneapolis, MN</div>
                  <div className="text-[10px] md:text-[11px] tracking-[0.2em] text-[#6B6B6B] uppercase" style={{ fontFamily: "'Space Mono', monospace" }}>1304 E Lake St., Suite 200 · 55407</div>
                </div>
              </div>
            </div>
            <div className="md:col-span-7 md:pl-12">
              <h2 className="text-[52px] sm:text-[68px] md:text-[88px] font-black leading-[0.9] text-[#F5F5F5] mb-10 md:mb-16" style={{ fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '-0.02em' }}>
                BUILT ON<br />MINNEAPOLIS<br /><span style={{ color: '#00E5FF' }}>VALUES</span>
              </h2>
              <div className="space-y-6 md:space-y-8 mb-12 md:mb-20">
                <p className="text-[14px] md:text-[16px] font-light leading-[2] text-[#6B6B6B]" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  Founded by <strong className="text-[#F5F5F5] font-medium">Abdimajeed Isse</strong>, Sure Kleen Building Services, Inc. is a leading minority-owned, bonded, and insured provider of comprehensive commercial and residential cleaning and facility maintenance solutions.
                </p>
                <p className="text-[14px] md:text-[16px] font-light leading-[2] text-[#6B6B6B]" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  Serving the entire Minneapolis–St. Paul metropolitan area, we bring both elite service standards and a commitment to community empowerment to every contract we fulfill — from federal government facilities to private residences.
                </p>
              </div>
              <div className="grid grid-cols-3 gap-0 mb-12 md:mb-20">
                <AnimatedStat value={16} suffix="+" label="Years in Business" />
                <AnimatedStat value={100} suffix="%" label="Client Satisfaction" />
                <AnimatedStat value={100} suffix="%" label="On-Time Delivery" />
              </div>
              <div className="flex flex-wrap gap-2 md:gap-3">
                {['SBA 8(a)', 'MBE', 'DBE', 'Minority Owned', 'Bonded', 'Insured', 'Targeted Group'].map((tag) => (
                  <span key={tag} className="px-3 md:px-4 py-1.5 md:py-2 border border-[#2E2E2E] text-[9px] md:text-[10px] tracking-[0.2em] text-[#6B6B6B] uppercase hover:border-[#00E5FF] hover:text-[#00E5FF] transition-all duration-300 cursor-default" style={{ fontFamily: "'Space Mono', monospace" }}>{tag}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── OUR STANDARDS ── */}
      <section id="impact" className="bg-[#0C0C0C] py-20 md:py-40 px-5 sm:px-10 md:px-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.05]">
          <img
            src="https://readdy.ai/api/search-image?query=bright-modern-glass-office-building-interior-with-clean-white-surfaces-and-geometric-steel-structure-natural-light-flooding-through-large-windows-minimalist-corporate-architecture-high-contrast-architectural-photography&width=1920&height=1080&seq=standards-bg-001&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="max-w-[1600px] mx-auto relative">
          <SectionLabel text="Our Standards" index="04" />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-32 items-center mb-16 md:mb-32">
            <h2 className="text-[52px] sm:text-[72px] md:text-[96px] font-black leading-[0.9] text-[#F5F5F5]" style={{ fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '-0.02em' }}>
              BUILT FOR<br />THOSE WHO<br /><span style={{ color: '#00E5FF' }}>DEMAND MORE</span>
            </h2>
            <p className="text-[14px] md:text-[16px] font-light leading-[2] text-[#6B6B6B]" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Every contract we take on is treated as a federal-grade obligation. Our processes, products, and people are held to the highest standards — because the environments we maintain reflect the organizations that trust us.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-[1px] bg-[#2E2E2E] mb-16 md:mb-32">
            {[
              { icon: 'ri-shield-check-line', title: 'Federal-Grade Compliance', desc: 'SBA 8(a) certified and ORCA registered — fully equipped for government and prime contractor engagements.' },
              { icon: 'ri-leaf-line', title: 'Eco-Responsible Products', desc: 'We prioritize biodegradable, low-impact cleaning solutions that protect both your facility and the environment.' },
              { icon: 'ri-time-line', title: 'Reliable Scheduling', desc: 'Consistent, on-time service delivery with real-time communication and flexible scheduling for any facility size.' },
              { icon: 'ri-team-line', title: 'Trained & Vetted Staff', desc: 'Every team member is background-checked, trained to industry standards, and committed to discretion and professionalism.' },
            ].map((pillar, i) => (
              <div key={i} className="bg-[#080808] p-8 md:p-12 group hover:bg-[#1C1C1C] transition-colors duration-300 cursor-pointer">
                <div className="w-10 h-10 flex items-center justify-center mb-6 md:mb-8">
                  <i className={`${pillar.icon} text-[26px] md:text-[28px] text-[#2E2E2E] group-hover:text-[#00E5FF] transition-colors duration-300`}></i>
                </div>
                <h3 className="text-[20px] md:text-[22px] font-bold text-[#F5F5F5] mb-3 md:mb-4 leading-tight" style={{ fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '0.04em' }}>{pillar.title}</h3>
                <p className="text-[12px] md:text-[13px] font-light leading-[1.9] text-[#6B6B6B]" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{pillar.desc}</p>
              </div>
            ))}
          </div>

          <SectionLabel text="Who We Serve" index="04.1" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-[1px] bg-[#2E2E2E]">
            {[
              { title: 'Corporate Offices', desc: 'Executive suites and commercial headquarters requiring impeccable, consistent standards.', icon: 'ri-building-2-line' },
              { title: 'Healthcare Facilities', desc: 'Medical centers and clinics demanding sterile, compliant, and safe environments.', icon: 'ri-hospital-line' },
              { title: 'Government & Federal', desc: 'SBA 8(a) certified for federal contracts and government facility service engagements.', icon: 'ri-government-line' },
              { title: 'Life Sciences & Research', desc: 'Laboratories and research facilities requiring specialized protocols and contamination control.', icon: 'ri-microscope-line' },
            ].map((client, i) => (
              <div key={i} className="bg-[#080808] p-8 md:p-12 group hover:bg-[#1C1C1C] transition-colors duration-300 cursor-pointer">
                <div className="w-10 h-10 flex items-center justify-center mb-6 md:mb-8">
                  <i className={`${client.icon} text-[26px] md:text-[28px] text-[#2E2E2E] group-hover:text-[#00E5FF] transition-colors duration-300`}></i>
                </div>
                <h3 className="text-[20px] md:text-[22px] font-bold text-[#F5F5F5] mb-3 md:mb-4 leading-tight" style={{ fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '0.04em' }}>{client.title}</h3>
                <p className="text-[12px] md:text-[13px] font-light leading-[1.9] text-[#6B6B6B]" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{client.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CONTACT ── */}
      <section id="contact" className="bg-[#080808] py-20 md:py-40 px-5 sm:px-10 md:px-20">
        <div className="max-w-[1600px] mx-auto">
          <SectionLabel text="Get In Touch" index="05" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-32 items-start mb-16 md:mb-32">
            <h2 className="text-[56px] sm:text-[72px] md:text-[96px] font-black leading-[0.9] text-[#F5F5F5]" style={{ fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '-0.02em' }}>
              LET&apos;S WORK<br /><span style={{ color: '#00E5FF' }}>TOGETHER</span>
            </h2>
            <div className="pt-0 md:pt-4 space-y-0">
              {[
                { icon: 'ri-phone-line', label: 'Phone', value: '(612) 607-9152', href: 'tel:6126079152' },
                { icon: 'ri-mail-line', label: 'Email', value: 'surekleen@gmail.com', href: 'mailto:surekleen@gmail.com' },
                { icon: 'ri-map-pin-line', label: 'Address', value: '1304 E Lake St., Suite 200\nMinneapolis, MN 55407', href: '#' },
              ].map((contact, i) => (
                <a key={i} href={contact.href} className="flex items-start gap-6 md:gap-8 py-7 md:py-10 border-b border-[#2E2E2E] group hover:border-[#00E5FF] transition-all duration-300 cursor-pointer">
                  <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 mt-1">
                    <i className={`${contact.icon} text-[18px] md:text-[20px] text-[#2E2E2E] group-hover:text-[#00E5FF] transition-colors duration-300`}></i>
                  </div>
                  <div>
                    <div className="text-[9px] tracking-[0.3em] text-[#6B6B6B] uppercase mb-2" style={{ fontFamily: "'Space Mono', monospace" }}>{contact.label}</div>
                    <div className="text-[16px] md:text-[20px] font-medium text-[#F5F5F5] group-hover:text-[#00E5FF] transition-colors duration-300 whitespace-pre-line" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{contact.value}</div>
                  </div>
                </a>
              ))}
            </div>
          </div>

          {/* CTA Banner */}
          <div className="relative overflow-hidden p-8 sm:p-12 md:p-20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-8" style={{ border: '1px solid #2E2E2E' }}>
            <div className="absolute inset-0 opacity-10" style={{ background: 'linear-gradient(135deg, #00E5FF 0%, transparent 60%)' }}></div>
            <div className="relative">
              <div className="text-[10px] tracking-[0.3em] text-[#00E5FF] uppercase mb-4" style={{ fontFamily: "'Space Mono', monospace" }}>Secure Access</div>
              <h3 className="text-[40px] md:text-[56px] font-black text-[#F5F5F5] leading-tight" style={{ fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '-0.01em' }}>CLIENT PORTAL</h3>
              <p className="text-[13px] md:text-[14px] font-light text-[#6B6B6B] mt-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Facility oversight, scheduling, and real-time reporting</p>
            </div>
            <button onClick={() => setShowPortal(true)}
              className="relative px-8 md:px-12 py-4 md:py-5 bg-[#00E5FF] text-[#080808] text-[11px] md:text-[12px] font-bold tracking-[0.25em] uppercase hover:bg-[#F5F5F5] transition-all duration-300 whitespace-nowrap cursor-pointer flex-shrink-0"
              style={{ fontFamily: "'Space Mono', monospace" }}
            >
              Access Portal →
            </button>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="bg-[#0C0C0C] pt-16 md:pt-32 pb-10 md:pb-16 px-5 sm:px-10 md:px-20" style={{ borderTop: '1px solid #2E2E2E' }}>
        <div className="max-w-[1600px] mx-auto">
          <div className="overflow-hidden mb-12 md:mb-24">
            <div className="text-[clamp(40px,10vw,180px)] font-black leading-none text-[#F5F5F5]/[0.04] tracking-[-4px] select-none" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              SURE KLEEN
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-10 md:gap-16 mb-12 md:mb-24">
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-3 mb-6 md:mb-8">
                <div className="w-7 h-7 border border-[#00E5FF] flex items-center justify-center flex-shrink-0">
                  <div className="w-2.5 h-2.5 bg-[#00E5FF]" style={{ clipPath: 'polygon(50% 0%, 100% 100%, 0% 100%)' }}></div>
                </div>
                <div>
                  <div className="text-[12px] font-bold tracking-[0.18em] text-[#F5F5F5] uppercase" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Sure Kleen</div>
                  <div className="text-[7px] font-light tracking-[0.3em] text-[#6B6B6B] uppercase" style={{ fontFamily: "'Space Mono', monospace" }}>Building Services, Inc.</div>
                </div>
              </div>
              <div className="space-y-2 text-[12px] font-light text-[#6B6B6B]" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                <p>1304 E Lake St., Suite 200</p>
                <p>Minneapolis, MN 55407</p>
                <p className="pt-2">Founded by Abdimajeed Isse</p>
                <p className="pt-2 text-[10px] tracking-[0.15em] uppercase" style={{ fontFamily: "'Space Mono', monospace" }}>Cage: 38BS9 · DUNS: 171838126</p>
              </div>
            </div>
            <div>
              <h4 className="text-[9px] tracking-[0.3em] text-[#6B6B6B] uppercase mb-6 md:mb-8" style={{ fontFamily: "'Space Mono', monospace" }}>Contact</h4>
              <div className="space-y-4 md:space-y-5">
                <a href="tel:6126079152" className="flex items-center gap-3 text-[12px] md:text-[13px] font-light text-[#6B6B6B] hover:text-[#00E5FF] transition-colors duration-300 cursor-pointer" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  <i className="ri-phone-line text-[14px]"></i>(612) 607-9152
                </a>
                <a href="mailto:surekleen@gmail.com" className="flex items-center gap-3 text-[12px] md:text-[13px] font-light text-[#6B6B6B] hover:text-[#00E5FF] transition-colors duration-300 cursor-pointer break-all" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  <i className="ri-mail-line text-[14px] flex-shrink-0"></i>surekleen@gmail.com
                </a>
              </div>
            </div>
            <div>
              <h4 className="text-[9px] tracking-[0.3em] text-[#6B6B6B] uppercase mb-6 md:mb-8" style={{ fontFamily: "'Space Mono', monospace" }}>Certifications</h4>
              <div className="flex flex-wrap gap-2">
                {['SBA 8(a)', 'MBE', 'DBE', 'Bonded', 'Insured', 'Targeted Group'].map((tag) => (
                  <span key={tag} className="px-2.5 py-1.5 border border-[#2E2E2E] text-[9px] tracking-[0.15em] text-[#6B6B6B] uppercase" style={{ fontFamily: "'Space Mono', monospace" }}>{tag}</span>
                ))}
              </div>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pt-8" style={{ borderTop: '1px solid #2E2E2E' }}>
            <p className="text-[9px] md:text-[10px] tracking-[0.15em] text-[#6B6B6B]/60 uppercase" style={{ fontFamily: "'Space Mono', monospace" }}>© 2026 Sure Kleen Building Services, Inc. · All rights reserved.</p>
            <p className="text-[9px] md:text-[10px] tracking-[0.15em] text-[#6B6B6B]/60 uppercase" style={{ fontFamily: "'Space Mono', monospace" }}>Minneapolis, MN · NAICS 561720 · 236220-15</p>
          </div>
        </div>
      </footer>

      {/* ── CLIENT PORTAL MODAL ── */}
      {showPortal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center px-4" style={{ background: 'rgba(8,8,8,0.92)', backdropFilter: 'blur(16px)' }} onClick={() => setShowPortal(false)}>
          <div className="w-full max-w-[560px] p-8 sm:p-12 md:p-16 relative" style={{ background: '#0C0C0C', border: '1px solid #2E2E2E' }} onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setShowPortal(false)} className="absolute top-6 right-6 md:top-8 md:right-8 w-8 h-8 flex items-center justify-center text-[#6B6B6B] hover:text-[#F5F5F5] transition-colors cursor-pointer">
              <i className="ri-close-line text-[20px]"></i>
            </button>
            <div className="mb-8 md:mb-12">
              <div className="text-[9px] tracking-[0.35em] text-[#00E5FF] uppercase mb-4" style={{ fontFamily: "'Space Mono', monospace" }}>[Secure Access]</div>
              <h3 className="text-[36px] md:text-[48px] font-black text-[#F5F5F5] leading-tight" style={{ fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '-0.01em' }}>CLIENT PORTAL</h3>
            </div>
            <form className="space-y-0" onSubmit={(e) => e.preventDefault()}>
              <div className="border-b border-[#2E2E2E] focus-within:border-[#00E5FF] transition-colors duration-300">
                <input type="email" placeholder="Email Address" className="w-full bg-transparent py-4 md:py-5 text-[13px] md:text-[14px] font-light text-[#F5F5F5] placeholder-[#6B6B6B]/60 focus:outline-none" style={{ fontFamily: "'Space Grotesk', sans-serif" }} />
              </div>
              <div className="border-b border-[#2E2E2E] focus-within:border-[#00E5FF] transition-colors duration-300">
                <input type="password" placeholder="Password" className="w-full bg-transparent py-4 md:py-5 text-[13px] md:text-[14px] font-light text-[#F5F5F5] placeholder-[#6B6B6B]/60 focus:outline-none" style={{ fontFamily: "'Space Grotesk', sans-serif" }} />
              </div>
              <div className="pt-8 md:pt-10">
                <button type="submit" className="w-full py-4 md:py-5 bg-[#00E5FF] text-[#080808] text-[11px] font-bold tracking-[0.3em] uppercase hover:bg-[#F5F5F5] transition-all duration-300 whitespace-nowrap cursor-pointer" style={{ fontFamily: "'Space Mono', monospace" }}>Access Portal</button>
              </div>
              <div className="pt-6 md:pt-8 text-center">
                <a href="mailto:surekleen@gmail.com" className="text-[10px] tracking-[0.2em] text-[#6B6B6B] hover:text-[#00E5FF] uppercase transition-colors duration-300 cursor-pointer" style={{ fontFamily: "'Space Mono', monospace" }}>Request Access · surekleen@gmail.com</a>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
